import React, { useMemo, useCallback, useState, useEffect } from "react";
import { useLocation, useNavigate, NavLink, useSearchParams } from "react-router-dom";
import { Tooltip, Modal, Badge } from "antd";
import {
  AppstoreOutlined,
  CloseOutlined,
  DashboardOutlined,
  ShoppingCartOutlined,
  BarChartOutlined,
  DatabaseOutlined,
  PlusOutlined,
  SettingOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  GlobalOutlined,
  ControlOutlined,
  NotificationOutlined,
  CarOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  LinkOutlined,
  LineChartOutlined,
  HomeOutlined,
  QrcodeOutlined,
  MessageOutlined,
  FileSearchOutlined,
} from "@ant-design/icons";
import { SendoSvg, ShopeeSvg, SvgTiktok, LazadaSvg } from "../SvgIcon";
import { http, settings } from "../../utils/config";
import { fetchIntegrations } from "../../pages/Content/carriers/integrationApi";
import { useSelector } from "react-redux";
import "./Sidebar.css";

const IS_DEV = process.env.NODE_ENV !== "production";

/* ─────────────────────────────────────────────
   Status Configuration
   ───────────────────────────────────────────── */
const STATUS_CONFIG = {
  connected: { label: "Đã kết nối", cssVar: "var(--sb-badge-connected)" },
  live: { label: "LIVE", cssVar: "var(--sb-badge-connected)" },
  suspended: { label: "Tạm ngưng", cssVar: "var(--sb-badge-expiring)" },
  expiring: { label: "Sắp hết hạn", cssVar: "var(--sb-badge-expiring)" },
  error: { label: "Lỗi kết nối", cssVar: "var(--sb-badge-error)" },
  not_connected: { label: "Chưa kết nối", cssVar: "var(--sb-badge-disconnected)" },
};

/**
 * Determine channel connection status from cookie.
 * Returns: "connected" | "not_connected"
 * NOTE: "expiring" and "error" states require API health check integration.
 * Override via statusOverrides prop when backend provides real status.
 */
function getStatusFromCookie(cookieName) {
  return settings.getCookie(cookieName) ? "connected" : "not_connected";
}

/* ─────────────────────────────────────────────
   Channel Definitions
   ───────────────────────────────────────────── */
const ECOMMERCE_CHANNELS = [
  {
    key: "shopee",
    label: "Shopee",
    icon: ShopeeSvg,
    route: IS_DEV ? "/new-sync-shopee" : "/ecommerce",
    cookieKey: "token",
    connectRoute: null, // OAuth — handled by /ecommerce hub
  },
  {
    key: "lazada",
    label: "Lazada",
    icon: LazadaSvg,
    route: "/add-product-lazada",
    cookieKey: "token_lazada",
    connectRoute: null, // OAuth — handled by /ecommerce hub
  },
  {
    key: "tiktok",
    label: "TikTok Shop",
    icon: SvgTiktok,
    route: "/sync-Tiktok",
    cookieKey: "access_tokenTikTok",
    connectRoute: null, // OAuth — handled by /ecommerce hub
  },
  {
    key: "tiki",
    label: "Tiki",
    logoUrl: "/carrier-logos/logo-tiki.png",
    route: "/add-product-tiki",
    cookieKey: "access_tokenTiki",
    connectRoute: null, // OAuth — handled by /ecommerce hub
  },
  {
    key: "sendo",
    label: "Sendo",
    icon: SendoSvg,
    route: "/create-product",
    cookieKey: "sendo_token",
    connectRoute: "/connectSendo",
  },
];
const VISIBLE_ECOMMERCE_CHANNELS = ECOMMERCE_CHANNELS.filter(
  (channel) => IS_DEV || channel.key !== "shopee"
);

/* Shipping channels are now driven by backend integration API.
   This static list is only used as fallback structure. */
const STATIC_SHIPPING_CHANNELS = [
  { key: "ghn", label: "Giao Hàng Nhanh", brandColor: "#ee4d2d" },
  { key: "vtp", label: "Viettel Post", brandColor: "#d70018" },
  { key: "grab", label: "Grab Express", brandColor: "#00b14f" },
  { key: "ahm", label: "Ahamove", brandColor: "#f5a623" },
  { key: "spx", label: "SPX Express", brandColor: "#ee4d2d" },
  { key: "jnt", label: "J&T Express", brandColor: "#d71920" },
  { key: "ghtk", label: "GHTK", brandColor: "#3aaa35" },
  { key: "ninja", label: "Ninja Van", brandColor: "#c81e1e" },
  { key: "best", label: "Best Express", brandColor: "#ff6600" },
  { key: "ems", label: "EMS Vietnam", brandColor: "#003399" },
];

/* ─────────────────────────────────────────────
   StatusBadge — Connection indicator dot
   ───────────────────────────────────────────── */
function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.not_connected;
  return (
    <Tooltip title={config.label} placement="right">
      <span
        className={`bota-sidebar-badge${status === "error" ? " bota-sidebar-badge--error" : ""}`}
        style={{ backgroundColor: config.cssVar }}
        role="status"
        aria-label={config.label}
      />
    </Tooltip>
  );
}

/* ─────────────────────────────────────────────
   ShippingIcon — Colored square with initial
   For shipping providers without custom SVG
   ───────────────────────────────────────────── */
function ShippingIcon({ color, label }) {
  return (
    <span className="bota-sidebar-shipping-icon" style={{ backgroundColor: color }}>
      {label.charAt(0)}
    </span>
  );
}

/* ─────────────────────────────────────────────
   SidebarItem — Single navigation entry
   Handles: default, hover, active, focus, disabled
   ───────────────────────────────────────────── */
function SidebarItem({ icon, label, route, badge, isActive, onClick, disabled, collapsed }) {
  const handleClick = () => {
    if (!disabled && onClick) onClick(route);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    }
  };

  const content = (
    <button
      className={[
        "bota-sidebar-item",
        isActive && "bota-sidebar-item--active",
        disabled && "bota-sidebar-item--disabled",
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-current={isActive ? "page" : undefined}
      disabled={disabled}
      role="menuitem"
      tabIndex={0}
    >
      <span className="bota-sidebar-item-icon">{icon}</span>
      <span className="bota-sidebar-item-label">{label}</span>
      {badge && <StatusBadge status={badge} />}
    </button>
  );

  /* In collapsed mode, wrap with tooltip to show label */
  if (collapsed) {
    return (
      <Tooltip title={label} placement="right">
        {content}
      </Tooltip>
    );
  }
  return content;
}

/* ─────────────────────────────────────────────
   SIDEBAR — Main Component
   ───────────────────────────────────────────── */
export default function Sidebar({ collapsed, onToggleCollapse, statusOverrides, isMobile, mobileOpen, onMobileClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userData } = useSelector((state) => state.persistedReducer.Login);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [shippingCarriers, setShippingCarriers] = useState([]);

  /* Fetch low-stock count on mount and when navigating away from /inventory */
  useEffect(() => {
    if (!userData?.idw) return;
    let cancelled = false;
    http.post("/inventory/low-stock-count", { idw: userData.idw })
      .then((res) => {
        if (!cancelled) setLowStockCount(res.data?.data?.count || 0);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [userData?.idw, location.pathname]);

  /* Fetch ALL shipping carrier statuses from integration API (cached 1-min) */
  useEffect(() => {
    if (!userData?.idw) return;
    let cancelled = false;
    fetchIntegrations(userData.idw)
      .then((result) => {
        if (cancelled) return;
        const items = result.integrations || result;
        if (Array.isArray(items)) {
          const visible = items.filter((c) => {
            // GHTK: luôn hiển thị trong sidebar nếu backend có trả về integration
            if ((c.key || c.code) === "ghtk") return true;
            return c.is_visible !== false;
          });
          const statusRank = { connected: 4, suspended: 3, error: 2, expiring: 1, not_connected: 0 };
          const byKey = {};
          visible.forEach((c) => {
            const key = c.key || c.code;
            const fallback = STATIC_SHIPPING_CHANNELS.find((s) => s.key === key);
            let status = "not_connected";
            if (c.admin_enabled === false) {
              status = "not_connected";
            } else if (c.status === "connected" && c.is_enabled) {
              status = "connected";
            } else if (c.status === "connected" && !c.is_enabled) {
              status = "suspended";
            } else if (c.status === "suspended") {
              status = "suspended";
            } else if (c.status === "error" || c.status === "auth_failed" || c.status === "api_unreachable") {
              status = "error";
            } else if (c.status === "expired") {
              status = "expiring";
            }
            const entry = byKey[key];
            if (!entry || statusRank[status] > statusRank[entry.status]) {
              byKey[key] = {
                key,
                label: c.name || fallback?.label || key,
                brandColor: c.brand_color || fallback?.brandColor || "#999",
                status,
              };
            }
          });
          setShippingCarriers(Object.values(byKey));
        }
      })
      .catch(() => {
        // API fails → use static fallback with not_connected
        setShippingCarriers(
          STATIC_SHIPPING_CHANNELS.map((ch) => ({ ...ch, status: "not_connected" }))
        );
      });
    return () => { cancelled = true; };
  }, [userData?.idw, location.pathname]);

  /* Build connection statuses for ecommerce channels (cookie-based) */
  const ecommerceStatuses = useMemo(() => {
    const result = {};
    VISIBLE_ECOMMERCE_CHANNELS.forEach((ch) => {
      result[ch.key] = statusOverrides?.[ch.key] || getStatusFromCookie(ch.cookieKey);
    });
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, statusOverrides]);

  const handleNavigate = useCallback(
    (route) => {
      onMobileClose?.();
      navigate(route);
    },
    [navigate, onMobileClose]
  );

  /** Shipping carrier click: navigate to /vanchuyen page (unified hub) */
  const handleShippingClick = useCallback(
    (channel) => {
      onMobileClose?.();
      if (channel.status === "connected") {
        navigate(`/order?carrier=${channel.key}`);
      } else {
        navigate("/vanchuyen");
      }
    },
    [navigate, onMobileClose]
  );

  /** Ecommerce channel click: go to product route or warn if not connected */
  const handleEcommerceClick = useCallback(
    (channel) => {
      onMobileClose?.();
      const connected = ecommerceStatuses[channel.key] === "connected";
      if (!connected) {
        const target = channel.connectRoute || "/ecommerce";
        Modal.info({
          title: `Chưa kết nối ${channel.label}`,
          content: `Bạn cần kết nối ${channel.label} trước khi quản lý sản phẩm.`,
          okText: "Kết nối ngay",
          onOk: () => navigate(target),
        });
        return;
      }
      navigate(channel.route);
    },
    [ecommerceStatuses, navigate, onMobileClose]
  );

  /** Check if a route is currently active */
  const isActive = useCallback(
    (route) => {
      if (route === "/") return location.pathname === "/";
      return location.pathname === route || location.pathname.startsWith(route + "/");
    },
    [location.pathname]
  );

  /** Check if a shipping carrier is the active filter */
  const activeCarrierKey = location.pathname === "/order" ? searchParams.get("carrier") : null;

  /** Check if any route in a group is active (for section highlighting) */
  const isSettingsActive =
    location.pathname === "/ecommerce" || location.pathname === "/vanchuyen";

  const sidebarClass = [
    "bota-sidebar",
    collapsed ? " bota-sidebar--collapsed" : "",
    isMobile ? " bota-sidebar--mobile" : "",
    isMobile && mobileOpen ? " bota-sidebar--mobile-open" : "",
  ].filter(Boolean).join("");

  return (
    <>
      {isMobile && mobileOpen && (
        <div
          className="bota-sidebar-backdrop"
          role="button"
          tabIndex={0}
          onClick={onMobileClose}
          onKeyDown={(e) => e.key === "Escape" && onMobileClose()}
          aria-label="Đóng menu"
        />
      )}
      <aside
        className={sidebarClass}
        role="navigation"
        aria-label="Main navigation"
      >
        {/* ─── Header: collapse toggle (desktop) / close (mobile) ─── */}
        <div className="bota-sidebar-header">
          <button
            className="bota-sidebar-toggle-btn"
            onClick={isMobile ? onMobileClose : onToggleCollapse}
            aria-label={isMobile ? "Đóng menu" : (collapsed ? "Mở rộng sidebar" : "Thu gọn sidebar")}
          >
            {isMobile ? <CloseOutlined /> : (collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />)}
          </button>
        </div>

      {/* ─── Scrollable Navigation ─── */}
      <nav className="bota-sidebar-nav" role="menu" aria-label="Site navigation">
        {/* ━━━ General ━━━ */}
        <div className="bota-sidebar-section">
          <SidebarItem
            icon={<AppstoreOutlined />}
            label="Dashboard"
            route="/dashboard"
            isActive={isActive("/dashboard")}
            onClick={handleNavigate}
            collapsed={collapsed}
          />
          <SidebarItem
            icon={<DashboardOutlined />}
            label="Tổng quan"
            route="/"
            isActive={isActive("/")}
            onClick={handleNavigate}
            collapsed={collapsed}
          />
          <SidebarItem
            icon={<ShoppingCartOutlined />}
            label="Đơn hàng"
            route="/order"
            isActive={isActive("/order")}
            onClick={handleNavigate}
            collapsed={collapsed}
          />
          <SidebarItem
            icon={<BarChartOutlined />}
            label="Doanh thu"
            route="/report/revenue"
            isActive={isActive("/report/revenue")}
            onClick={handleNavigate}
            collapsed={collapsed}
          />
          <SidebarItem
            icon={
              <Badge count={lowStockCount} size="small" offset={[2, -2]}>
                <DatabaseOutlined style={{ fontSize: 16 }} />
              </Badge>
            }
            label="Tồn kho"
            route="/inventory"
            isActive={isActive("/inventory")}
            onClick={handleNavigate}
            collapsed={collapsed}
          />
          <SidebarItem
            icon={<TeamOutlined style={{ fontSize: 16 }} />}
            label="Khách hàng"
            route="/customers"
            isActive={isActive("/customers")}
            onClick={handleNavigate}
            collapsed={collapsed}
          />
          <SidebarItem
            icon={<LineChartOutlined style={{ fontSize: 16 }} />}
            label="Analytics v2"
            route="/analytics"
            isActive={isActive("/analytics")}
            onClick={handleNavigate}
            collapsed={collapsed}
          />
          <SidebarItem
            icon={<HomeOutlined style={{ fontSize: 16 }} />}
            label="Kho hàng"
            route="/warehouses"
            isActive={isActive("/warehouses")}
            onClick={handleNavigate}
            collapsed={collapsed}
          />
          <SidebarItem
            icon={<QrcodeOutlined style={{ fontSize: 16 }} />}
            label="Barcode / QR"
            route="/inventory/barcode"
            isActive={isActive("/inventory/barcode")}
            onClick={handleNavigate}
            collapsed={collapsed}
          />
          {process.env.NODE_ENV === "development" && (
            <SidebarItem
              icon={<MessageOutlined style={{ fontSize: 16 }} />}
              label="Chat"
              route="/chat"
              isActive={isActive("/chat")}
              onClick={handleNavigate}
              collapsed={collapsed}
            />
          )}
        </div>

        <hr className="bota-sidebar-divider" />

        {/* ━━━ KÊNH BÁN HÀNG ━━━ */}
        <div className="bota-sidebar-section">
          <div className="bota-sidebar-section-label" id="section-ecommerce">
            Kênh bán hàng
          </div>

          {/* CTA: Đăng sản phẩm — routes to PostProductHub */}
          {collapsed ? (
            <Tooltip title="Đăng sản phẩm" placement="right">
              <button
                className="bota-sidebar-cta"
                onClick={() => handleNavigate("/post-product")}
                aria-label="Đăng sản phẩm"
                role="menuitem"
              >
                <PlusOutlined />
                <span className="bota-sidebar-cta-label">Đăng sản phẩm</span>
              </button>
            </Tooltip>
          ) : (
            <button
              className="bota-sidebar-cta"
              onClick={() => handleNavigate("/post-product")}
              aria-describedby="section-ecommerce"
              role="menuitem"
            >
              <PlusOutlined />
              <span className="bota-sidebar-cta-label">Đăng sản phẩm</span>
            </button>
          )}

          <SidebarItem
            icon={<LinkOutlined style={{ fontSize: 16 }} />}
            label="Liên kết SP"
            route="/product-linking"
            isActive={isActive("/product-linking")}
            onClick={handleNavigate}
            collapsed={collapsed}
          />

          {/* Channel list */}
          {VISIBLE_ECOMMERCE_CHANNELS.map((channel) => (
            <SidebarItem
              key={channel.key}
              icon={
                <span className="bota-sidebar-icon-brand">
                  {channel.logoUrl ? (
                    <img src={channel.logoUrl} alt="" />
                  ) : (
                    channel.icon
                  )}
                </span>
              }
              label={channel.label}
              route={channel.route}
              badge={ecommerceStatuses[channel.key]}
              isActive={isActive(channel.route)}
              onClick={() => handleEcommerceClick(channel)}
              collapsed={collapsed}
            />
          ))}
        </div>

        <hr className="bota-sidebar-divider" />

        {/* ━━━ VẬN CHUYỂN ━━━ */}
        <div className="bota-sidebar-section">
          <div className="bota-sidebar-section-label" id="section-shipping">
            Vận chuyển
          </div>

          {/* Luôn hiển thị: vào trang quản lý vận chuyển (xem / thêm nhiều tài khoản) */}
          <SidebarItem
            icon={<CarOutlined />}
            label="Quản lý vận chuyển"
            route="/vanchuyen"
            isActive={isActive("/vanchuyen")}
            onClick={handleNavigate}
            collapsed={collapsed}
          />
          <SidebarItem
            icon={<ThunderboltOutlined style={{ fontSize: 16 }} />}
            label="So sánh giá"
            route="/shipping/compare"
            isActive={isActive("/shipping/compare")}
            onClick={handleNavigate}
            collapsed={collapsed}
          />

          {shippingCarriers.map((channel) => (
            <SidebarItem
              key={channel.key}
              icon={
                <ShippingIcon color={channel.brandColor} label={channel.label} />
              }
              label={channel.label}
              route="/vanchuyen"
              badge={channel.status}
              isActive={activeCarrierKey === channel.key}
              onClick={() => handleShippingClick(channel)}
              collapsed={collapsed}
            />
          ))}
        </div>

        <hr className="bota-sidebar-divider" />

        {/* ━━━ CẤU HÌNH ━━━ */}
        <div className="bota-sidebar-section">
          <div className="bota-sidebar-section-label" id="section-settings">
            Cấu hình
          </div>

          <SidebarItem
            icon={<SettingOutlined />}
            label="Cấu hình kết nối"
            route="/ecommerce"
            isActive={isSettingsActive}
            onClick={handleNavigate}
            collapsed={collapsed}
          />

          {userData?.is_admin && (
            <>
              <SidebarItem
                icon={<ControlOutlined />}
                label="Quản trị VC"
                route="/admin/shipping"
                isActive={isActive("/admin/shipping")}
                onClick={handleNavigate}
                collapsed={collapsed}
              />
              <SidebarItem
                icon={<NotificationOutlined />}
                label="Thông báo"
                route="/admin/announcements"
                isActive={isActive("/admin/announcements")}
                onClick={handleNavigate}
                collapsed={collapsed}
              />
              <SidebarItem
                icon={<FileSearchOutlined />}
                label="SEO trang web"
                route="/admin/seo"
                isActive={isActive("/admin/seo")}
                onClick={handleNavigate}
                collapsed={collapsed}
              />
            </>
          )}
        </div>
      </nav>

      {/* ─── Footer: View Website ─── */}
      {userData?.domain_web && (
        <div className="bota-sidebar-footer">
          <NavLink
            className="bota-sidebar-view-site"
            to={`https://${userData.domain_web}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <GlobalOutlined />
            <span>Xem trang</span>
          </NavLink>
        </div>
      )}
    </aside>
    </>
  );
}
